'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'

import { API } from '~/utils/api'
import { formatConsentToAPIFormat } from '~/utils/formatConsentToAPIFormat'
import { uploadPublicFile } from '~/utils/uploadPublicFile'

import { useCreateContestSubmissionMutation } from '~/app/generated/graphql'
import { Button } from '~/components/Button/Button'
import CheckboxView from '~/components/Checkbox'
import { DangerousTranslate } from '~/components/DangerousTranslate'
import { FileUpload } from '~/components/FileUpload'
import { FormInput } from '~/components/FormInput/FormInput'
import { useToast } from '~/components/Toast'
import { gt } from '~/lib/gt'

import styles from './Form.module.css'
import { contestMartiniDefaultValues, contestMartiniSchema } from './Form.utils'

enum FIELDS {
  NAME = 'name',
  EMAIL = 'email',
  PHONE_NUMBER = 'phoneNumber',
  FILE = 'file',
  QUESTION = 'question',
  ANSWER = 'answer',
  ELIGIBLE_TO_PARTICIPATE = 'eligibleToParticipate',
  DATA_PROCESSING = 'dataProcessing',
  MARKETINGS = 'marketings',
}

export function Form({
  translations,
  contestEditionId,
  consent,
}: {
  contestEditionId: string
  translations: {
    name: string
    email: string
    phoneNumber: string
    file: string
    question: string
    answer: string
    eligibleToParticipate: string
    dataProcessing: string
    button: string
    toastError: string
    toastSuccess: string
    fileUploadDescription: string
    expandBtn: {
      true: string
      false: string
    }
  }
  consent: any
}) {
  const toastState = useToast()

  const [createContestSubmission] = useCreateContestSubmissionMutation()

  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting, isValid, touchedFields },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: contestMartiniDefaultValues,
    resolver: yupResolver(contestMartiniSchema),
  })

  const sendContestSubmission = async (variables: any) => {
    setLoading(true)

    const { name, email, phoneNumber, file, text } = variables

    const fileData = await uploadPublicFile(API.pl, file[0])

    await createContestSubmission({
      variables: {
        name,
        email,
        phoneNumber,
        text,
        contestEditionId,
        imageFileId: fileData.id,
        agreementText: formatConsentToAPIFormat({
          ...consent,
          value: variables[FIELDS.MARKETINGS],
          label: <DangerousTranslate text={consent.label} />,
          showMore: <DangerousTranslate text={consent.showMore} />,
        }).agreementText,
      },
      onCompleted: () => {
        toastState?.add({
          type: 'success',
          message: translations.toastSuccess,
          containerSelector: '#ContestToastsContainer',
          size: 'regular',
        })
        reset()
      },
      onError: () => {
        toastState?.add({
          type: 'error',
          message: translations.toastError,
          containerSelector: '#ContestToastsContainer',
          size: 'regular',
        })
        reset()
      },
    })

    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit(sendContestSubmission)} className={styles.form}>
      <FormInput
        {...register(FIELDS.NAME)}
        label={translations[FIELDS.NAME]}
        errorMessage={touchedFields.name && errors.name?.message ? errors.name.message : ''}
      />

      <FormInput
        {...register(FIELDS.EMAIL)}
        label={translations[FIELDS.EMAIL]}
        errorMessage={touchedFields.email && errors.email?.message ? errors.email.message : ''}
      />

      <FormInput
        {...register(FIELDS.PHONE_NUMBER)}
        label={translations[FIELDS.PHONE_NUMBER]}
        errorMessage={touchedFields.phoneNumber && errors.phoneNumber?.message ? errors.phoneNumber.message : ''}
      />

      <div>
        <p className='text-sm' style={{ marginBottom: 8 }}>
          {translations[FIELDS.FILE]}
        </p>
        <FileUpload name={FIELDS.FILE} description={translations.fileUploadDescription} setValue={setValue} errors={errors} />
      </div>

      <div className={styles.questionBox}>
        <p className='text-sm'> {translations[FIELDS.QUESTION]} </p>
        <p className={`text-sm ${styles.answer}`}>{translations[FIELDS.ANSWER]}</p>
      </div>

      <div style={{ width: '100%' }}>
        <textarea {...register('text')} rows={10} className={styles.textarea} />
        {touchedFields.text && errors.text?.message && (
          <p className={styles.error}>{gt.tp('Error', touchedFields.text && errors.text?.message ? errors.text.message : '')}</p>
        )}
      </div>

      <CheckboxView
        control={control}
        name={FIELDS.ELIGIBLE_TO_PARTICIPATE}
        label={<DangerousTranslate text={translations[FIELDS.ELIGIBLE_TO_PARTICIPATE]} />}
        error={!!errors.eligibleToParticipate}
      />

      <CheckboxView
        control={control}
        name={FIELDS.DATA_PROCESSING}
        label={translations[FIELDS.DATA_PROCESSING]}
        error={!!errors.dataProcessing}
      />

      <CheckboxView
        control={control}
        name={FIELDS.MARKETINGS}
        error={!!errors.marketings}
        label={<DangerousTranslate text={consent.label} />}
        showMore={<DangerousTranslate text={consent.showMore} />}
        expandBtn={translations.expandBtn}
      />

      <Button type='submit' disabled={!isValid || isSubmitting} className={`${styles.formButton} ${loading && styles.loading}`}>
        <span className={styles.text}>{translations.button}</span>
        <span className={styles.loader}></span>
      </Button>

      <div id='ContestToastsContainer' className={styles.toastContainer} />
    </form>
  )
}
